// src/pages/customers/Edit.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../core/api';
import './Customers.css';
import CustomersSkeleton from './CustomersSkeleton';

const Edit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const nameInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    national: '',
    eco: '',
    postal: '',
    province: '',
    city: '',
    address: '',
    phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  // ردیابی تغییرات ذخیره نشده
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);

  /* ============== Fetch ============== */
  useEffect(() => {
    let cancelled = false;

    const fetchCustomer = async () => {
      try {
        const res = await api.get(`/customers/${id}`);
        const customer = res.data?.customer || res.data;

        if (!customer || !customer.id) {
          if (!cancelled) setNotFound(true);
          return;
        }

        if (!cancelled) {
          setForm({
            name: customer.name || '',
            national: customer.national || '',
            eco: customer.eco || '',
            postal: customer.postal || '',
            province: customer.province || '',
            city: customer.city || '',
            address: customer.address || '',
            phone: customer.phone || '',
          });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('خطا در دریافت مشتری:', err);

        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setMessage('خطا در دریافت اطلاعات مشتری');
          setMessageType('danger');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCustomer();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* ============== Toast auto-hide ============== */
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 3000);
    return () => clearTimeout(t);
  }, [message]);

  /* ============== beforeunload ============== */
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () =>
      window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  /* ============== Handlers ============== */
  const markDirty = () => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      setIsDirty(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    markDirty();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!form.phone.trim()) {
      setMessage('شماره موبایل الزامی است');
      setMessageType('danger');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        national: form.national.trim(),
        eco: form.eco.trim(),
        postal: form.postal.trim(),
        province: form.province.trim(),
        city: form.city.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
      };

      await api.post(`/customers/${id}/edit`, payload);

      // ریست dirty
      setIsDirty(false);
      isDirtyRef.current = false;

      setMessage('تغییرات با موفقیت ذخیره شد');
      setMessageType('success');

      // بازگشت به لیست بعد از ۱ ثانیه
      setTimeout(() => {
        navigate('/dashboard/customers');
      }, 1000);
    } catch (err) {
      console.error('خطا در ذخیره تغییرات:', err);
      setMessage(
        err.response?.data?.error || 'خطا در ذخیره تغییرات'
      );
      setMessageType('danger');
    } finally {
      setSaving(false);
    }
  };

  /* ============== Render ============== */
  if (loading) {
    return <CustomersSkeleton />;
  }

  if (notFound) {
    return (
      <div className="card">
        <h2>مشتری یافت نشد</h2>
        <p className="empty">
          مشتری‌ای با این شناسه وجود ندارد یا حذف شده است.
        </p>
        <div className="flexend">
          <Link className="btn secondary" to="/dashboard/customers">
            <i className="fa-solid fa-arrow-right" /> بازگشت به لیست
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {message && (
        <div
          className={
            messageType === 'danger' ? 'toast-danger' : 'toast-success'
          }
        >
          {message}
        </div>
      )}

      <div className="card">
        <h2>ویرایش مشتری</h2>

        {/* بنر تغییرات ذخیره نشده */}
        {isDirty && (
          <div className="dirty-banner">
            <i className="fa-solid fa-triangle-exclamation" />
            تغییرات ذخیره نشده دارید. لطفاً قبل از خروج ذخیره کنید.
          </div>
        )}

        <form onSubmit={handleSubmit} id="editCustomerForm">
          <div className="grid">
            <div>
              <label>نام شخص حقیقی/حقوقی *</label>
              <input
                ref={nameInputRef}
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label>شماره ثبت/شماره ملی</label>
              <input
                name="national"
                value={form.national}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>شماره اقتصادی</label>
              <input
                name="eco"
                value={form.eco}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>کد پستی</label>
              <input
                name="postal"
                value={form.postal}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>استان</label>
              <input
                name="province"
                value={form.province}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>شهرستان</label>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
              />
            </div>
            <div className="field-full">
              <label>نشانی کامل</label>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>شماره تلفن/موبایل *</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                placeholder="09xxxxxxxxx"
              />
            </div>
          </div>

          <div className="flexend">
            <Link
              className="btn secondary"
              to="/dashboard/customers"
              style={{ marginLeft: 8 }}
            >
              <i className="fa-solid fa-xmark" /> انصراف
            </Link>
            <button className="btn" type="submit" disabled={saving}>
              {!saving && <i className="fa-solid fa-floppy-disk" />}
              <span className="btn-label">
                {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
              </span>
              {saving && (
                <span className="btn-spinner">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 50 50"
                    style={{ animation: 'spin 0.8s linear infinite' }}
                  >
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray="90 150"
                    />
                  </svg>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Edit;
